// src/pages/AssessmentsPage/hooks/useAssessmentForm.js
import { useState, useEffect, useMemo } from "react";
import { useCreateSubjectAssessmentMutation } from "../../../store/api/subjectAssessmentApiSlice";
import { useGetClassByDepartmentQuery } from "../../../store/api/classApiSlice";
import {
	FIRST_SEMESTER_MONTHS,
	SECOND_SEMESTER_MONTHS,
} from "./useAssessmentsData";

export const ASSESSMENT_TYPES = [
	"Exam",
	"Quiz",
	"Project",
	"Homework",
	"Activity",
	"Book report",
	"Other",
];

const getFirstDayOfMonth = (month, selectedSemester) => {
	const monthIndex = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	].indexOf(month);

	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();

	let year = currentYear;

	if (selectedSemester === "First Semester") {
		if (
			currentMonth < 8 &&
			["September", "October", "November", "December"].includes(month)
		) {
			year = currentYear - 1;
		}
	} else if (selectedSemester === "Second Semester") {
		if (
			currentMonth >= 8 &&
			["February", "March", "April", "May"].includes(month)
		) {
			year = currentYear + 1;
		}
	}

	const monthStr = String(monthIndex + 1).padStart(2, "0");
	const isoString = `${year}-${monthStr}-01T12:00:00.000Z`;

	return new Date(isoString);
};

// Helper function to get month number from month name
const getMonthNumber = (monthName) => {
	const months = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];
	return months.indexOf(monthName) + 1;
};

// Helper function to get point limits based on grade level and month
const getPointLimits = (gradeLevel, monthName, selectedSemester) => {
	const monthNumber = getMonthNumber(monthName);

	if (gradeLevel <= 3) {
		return {
			monthly: 25,
			semester: 100,
		};
	}

	// For grades 4-12
	let monthly = 20; // Default for most months

	// Check if it's the last month of the semester (finals month)
	if (selectedSemester === "First Semester" && monthNumber === 12) {
		// December
		monthly = 40;
	} else if (selectedSemester === "Second Semester" && monthNumber === 5) {
		// May
		monthly = 40;
	}

	return {
		monthly,
		semester: 100,
	};
};

// Notification management hook
const useNotifications = () => {
	const [notifications, setNotifications] = useState([]);

	const addNotification = (type, message) => {
		const id = Date.now() + Math.random();
		const notification = { id, type, message };

		setNotifications((prev) => [...prev, notification]);

		// Auto-remove after 5 seconds
		setTimeout(() => {
			removeNotification(id);
		}, 5000);

		return id;
	};

	const removeNotification = (id) => {
		setNotifications((prev) => prev.filter((n) => n.id !== id));
	};

	return {
		notifications,
		addNotification,
		removeNotification,
	};
};

export const useAssessmentForm = ({
	selectedSemester,
	selectedMonth,
	selectedSubject,
	departmentId,
	user,
	refetchAssessments,
	isMobile,
	currentMonthAssessments = [],
}) => {
	const [formVisible, setFormVisible] = useState(false);
	const [title, setTitle] = useState("");
	const [type, setType] = useState("Exam");
	const [points, setPoints] = useState("");
	const [formMonth, setFormMonth] = useState("");

	const [createAssessment, { isLoading: isCreating }] =
		useCreateSubjectAssessmentMutation();

	// Get class data to determine grade level
	const { data: classData } = useGetClassByDepartmentQuery(departmentId, {
		skip: !departmentId,
	});

	// Notification management
	const { notifications, addNotification, removeNotification } =
		useNotifications();

	// Get grade level from class data
	const gradeLevel = useMemo(() => {
		if (!classData?.gradeLevel) return null;
		const level = parseInt(classData.gradeLevel);
		return isNaN(level) ? null : level;
	}, [classData]);

	// Calculate current point limits
	const pointLimits = useMemo(() => {
		if (!gradeLevel || !formMonth) return null;
		return getPointLimits(gradeLevel, formMonth, selectedSemester);
	}, [gradeLevel, formMonth, selectedSemester]);

	// Calculate current month's total points
	const currentMonthPoints = useMemo(() => {
		if (!currentMonthAssessments.length) return 0;
		return currentMonthAssessments.reduce((sum, assessment) => {
			return sum + (Number(assessment.points) || 0);
		}, 0);
	}, [currentMonthAssessments]);

	// Validation for point limits
	const validatePoints = (pointsToAdd) => {
		if (!pointLimits || !gradeLevel) return { valid: true };

		const pointsValue = Number(pointsToAdd) || 0;

		// Changed from 1 to 0.1 for minimum points
		if (pointsValue < 0.1) {
			return {
				valid: false,
				message: "Points must be at least 0.1",
			};
		}

		// Check monthly limit
		if (pointsValue > pointLimits.monthly) {
			return {
				valid: false,
				message: `Assessment points (${pointsValue}) exceed the monthly limit of ${pointLimits.monthly} points for grade ${gradeLevel}`,
			};
		}

		// Check if adding this assessment would exceed monthly limit
		const totalAfterAdding = currentMonthPoints + pointsValue;
		if (totalAfterAdding > pointLimits.monthly) {
			return {
				valid: false,
				message: `Adding this assessment would exceed the monthly point limit. Current total: ${currentMonthPoints}, New assessment: ${pointsValue}, Monthly limit: ${pointLimits.monthly}`,
			};
		}

		return { valid: true };
	};

	useEffect(() => {
		if (selectedMonth) {
			setFormMonth(selectedMonth);
		}
	}, [selectedMonth]);

	useEffect(() => {
		const monthsToDisplay =
			selectedSemester === "First Semester"
				? FIRST_SEMESTER_MONTHS
				: SECOND_SEMESTER_MONTHS;

		if (!formMonth || !monthsToDisplay.includes(formMonth)) {
			setFormMonth(monthsToDisplay[0]);
		}
	}, [selectedSemester, formMonth]);

	useEffect(() => {
		if (isMobile && formVisible) {
			setFormVisible(false);
		}
	}, [isMobile]);

	const handleAddAssessment = async () => {
		// Basic validation
		if (!title.trim()) {
			addNotification("error", "Please enter an assessment title");
			return;
		}

		if (!selectedSubject) {
			addNotification("error", "Please select a subject");
			return;
		}

		if (!formMonth) {
			addNotification("error", "Please select a month");
			return;
		}

		if (!user?.id) {
			console.error("No user ID available for assessment creation");
			addNotification(
				"error",
				"User authentication error. Please refresh and try again."
			);
			return;
		}

		// Validate points
		const pointsValue = Number(points) || 1;
		const pointValidation = validatePoints(pointsValue);

		if (!pointValidation.valid) {
			addNotification("error", pointValidation.message);
			return;
		}

		try {
			const assessmentDate = getFirstDayOfMonth(formMonth, selectedSemester);

			const assessmentData = {
				subjectId: selectedSubject,
				departmentId: departmentId,
				teacherId: user.id,
				title: title.trim(),
				type,
				points: pointsValue.toString(),
				date: assessmentDate.toISOString(),
			};

			await createAssessment(assessmentData).unwrap();

			// Success notification
			addNotification(
				"success",
				`Assessment "${title.trim()}" created successfully!`
			);

			// Reset form
			setTitle("");
			setType("Exam");
			setPoints("");
			setFormMonth(selectedMonth || SECOND_SEMESTER_MONTHS[0]);

			if (isMobile) {
				setFormVisible(false);
			}

			refetchAssessments();
		} catch (error) {
			console.error("Failed to create assessment:", error);

			// Handle different types of errors with user-friendly messages
			let errorMessage = "Failed to create assessment. Please try again.";

			if (error?.status === 400) {
				if (error?.data?.error) {
					// Backend validation error (like point limits)
					errorMessage = error.data.error;
				} else {
					errorMessage =
						"Invalid assessment data. Please check all fields and try again.";
				}
			} else if (error?.status === 401) {
				errorMessage = "You don't have permission to create assessments.";
			} else if (error?.status === 409) {
				errorMessage = "An assessment with this information already exists.";
			} else if (error?.status >= 500) {
				errorMessage = "Server error. Please try again later.";
			} else if (error?.data?.message) {
				errorMessage = error.data.message;
			} else if (error?.message) {
				errorMessage = error.message;
			}

			addNotification("error", errorMessage);
		}
	};

	const monthsToDisplay =
		selectedSemester === "First Semester"
			? FIRST_SEMESTER_MONTHS
			: SECOND_SEMESTER_MONTHS;

	return {
		// Original form state (no changes)
		formVisible,
		setFormVisible,
		title,
		setTitle,
		type,
		setType,
		points,
		setPoints,
		formMonth,
		setFormMonth,
		isCreating,
		monthsToDisplay,
		ASSESSMENT_TYPES,
		handleAddAssessment,

		// New notification functionality (only addition)
		notifications,
		removeNotification,
	};
};
